import {
  type QueryClient,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useCallback } from "react"
import { tintVTOService } from "services/tintvto"
import { capturePlatformEvent } from "services/platformAnalytics"
import { useAlert } from "providers/AlertProvider"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { ProductCategories, ProductCategoriesLink } from "@/components/Navbar/types"
import { withListPage } from "@/utils/withListPage"
import { type GlassesDraftListItem, type Product, type ProductV2 } from "types"
import { selectBillingEntitlement } from "store/billing/billingSlice"
import { type ProductType } from "store/product/types"
import { useAppSelector } from "hooks/storeHooks"
import { selectUserInfo } from "store/user/userSlice"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"

export const PRODUCTS_QUERY_KEY = (
  page?: number,
  searchText?: string,
  category?: ProductCategories,
  merchantId?: string,
  limit?: number
) =>
  page === undefined &&
  searchText === undefined &&
  category === undefined &&
  merchantId === undefined
    ? ["products"]
    : [
        "products",
        {
          page,
          searchText,
          category,
          merchantId,
          limit,
        },
      ]

export const ALL_PRODUCTS_QUERY_KEY = ["allProducts"]
export const ALL_GLASSES_DRAFTS_QUERY_KEY = ["glasses-drafts"]
export const GLASSES_DRAFTS_QUERY_KEY = (merchantId?: string, partial?: string) => [
  ...ALL_GLASSES_DRAFTS_QUERY_KEY,
  {
    merchantId,
    partial,
  },
]

export type ProductsQueryParams = {
  page: number
  searchText: string
  category: ProductCategories
  merchantId: string
  limit?: number
}

export type ProductsQueryResponse = {
  items: Product[]
  pageCount: number
}

type ProductsQuerySnapshot = Array<[QueryKey, ProductsQueryResponse | undefined]>

const snapshotProductQueries = (queryClient: QueryClient): ProductsQuerySnapshot =>
  queryClient.getQueriesData<ProductsQueryResponse>({
    queryKey: PRODUCTS_QUERY_KEY(),
  })

const restoreProductQueries = (
  queryClient: QueryClient,
  snapshots: ProductsQuerySnapshot | undefined
) => {
  snapshots?.forEach(([queryKey, data]) => {
    if (data) queryClient.setQueryData(queryKey, data)
  })
}

const toggleProductVisibilityInCachedLists = (
  queryClient: QueryClient,
  productId: string,
  nextVisible: boolean
) => {
  queryClient.setQueriesData<ProductsQueryResponse>({ queryKey: PRODUCTS_QUERY_KEY() }, (old) => {
    if (!old) return old

    return {
      ...old,
      items: old.items.map((product) =>
        product.id === productId ? { ...product, visible: nextVisible } : product
      ),
    }
  })
}

const removeProductFromCachedLists = (queryClient: QueryClient, productId: string) => {
  queryClient.setQueriesData<ProductsQueryResponse>({ queryKey: PRODUCTS_QUERY_KEY() }, (old) => {
    if (!old) return old

    return {
      ...old,
      items: old.items.filter((product) => product.id !== productId),
    }
  })
}

export const useProductClientClearCache = () => {
  const queryClient = useQueryClient()
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY() })
    void queryClient.invalidateQueries({ queryKey: ALL_PRODUCTS_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY })
  }, [queryClient])
}

export const useProductsQuery = ({
  page,
  searchText,
  category,
  merchantId,
  limit,
}: ProductsQueryParams) => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUserInfo)
  const billingEntitlement = useAppSelector(selectBillingEntitlement)
  const isAdmin = userInfo.groups.some((group) => group.trim().toLowerCase() === "admin")
  const requiresBillingAccess = !isAdmin
  const canLoadProducts =
    isAdmin || (Boolean(merchantId) && (!requiresBillingAccess || billingEntitlement?.entitled))

  return useQuery<ProductsQueryResponse>({
    enabled: canLoadProducts,
    queryKey: PRODUCTS_QUERY_KEY(page, searchText, category, merchantId, limit),
    queryFn: async () => {
      try {
        const { data } = await tintVTOService.productsV2(
          page - 1,
          searchText,
          category,
          merchantId,
          limit
        )
        return {
          items: (data?.productsV2?.items || []) as Product[],
          pageCount: data?.productsV2?.pageInfo?.pageQty || 0,
        }
      } catch {
        showAlert({
          error: true,
          text: getGenericUserErrorMessage(t),
        })
        throw new Error(getGenericUserErrorMessage(t))
      }
    },
    retry: false,
  })
}

export const useGlassesDraftsQuery = ({
  enabled = true,
  merchantId,
  partial,
}: {
  enabled?: boolean
  merchantId: string
  partial: string
}) => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const userInfo = useAppSelector(selectUserInfo)
  const billingEntitlement = useAppSelector(selectBillingEntitlement)
  const isAdmin = userInfo.groups.some((group) => group.trim().toLowerCase() === "admin")
  const requiresBillingAccess = !isAdmin
  const canLoadDrafts =
    isAdmin || (Boolean(merchantId) && (!requiresBillingAccess || billingEntitlement?.entitled))

  return useQuery<GlassesDraftListItem[]>({
    enabled: enabled && canLoadDrafts,
    queryKey: GLASSES_DRAFTS_QUERY_KEY(merchantId, partial),
    queryFn: async () => {
      try {
        return await tintVTOService.listGlassesDrafts({
          merchantId,
          partial,
        })
      } catch {
        showAlert({
          error: true,
          text: getGenericUserErrorMessage(t),
        })
        throw new Error(getGenericUserErrorMessage(t))
      }
    },
    retry: false,
  })
}

export const useAllProductsQuery = (merchantId: string) => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const clearCache = useProductClientClearCache()
  const userInfo = useAppSelector(selectUserInfo)
  const billingEntitlement = useAppSelector(selectBillingEntitlement)
  const isAdmin = userInfo.groups.some((group) => group.trim().toLowerCase() === "admin")
  const requiresBillingAccess = !isAdmin
  const canLoadProducts =
    isAdmin || (Boolean(merchantId) && (!requiresBillingAccess || billingEntitlement?.entitled))

  const query = useQuery<ProductType[]>({
    enabled: canLoadProducts,
    queryKey: [...ALL_PRODUCTS_QUERY_KEY, { merchantId }],
    queryFn: async () => {
      try {
        const makeup = await tintVTOService.fetchAllProducts(merchantId, ProductCategories.MAKEUP)
        const glasses = await tintVTOService.fetchAllProducts(merchantId, ProductCategories.GLASSES)
        return [...makeup, ...glasses]
      } catch {
        showAlert({
          error: true,
          text: getGenericUserErrorMessage(t),
        })
        throw new Error(getGenericUserErrorMessage(t))
      }
    },
    retry: false,
  })
  return {
    ...query,
    clearCache,
  }
}

export const useSaveProductMutation = (
  _merchantId: string,
  category: ProductCategories,
  onAfterSave?: (productId: string, skuIds: string[], translations: any) => Promise<void>,
  options: { navigateOnSuccess?: boolean; showSuccessToast?: boolean } = {}
) => {
  const clearCache = useProductClientClearCache()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { showAlert } = useAlert()

  return useMutation({
    mutationFn: async (product: ProductV2 & { pendingTranslations?: any }) => {
      const response = await tintVTOService.saveProductV2(product)

      // Get productId from response
      // saveProductV2 returns the product ID directly (as a number or string)
      const responseId = response.data?.saveProductV2
      const savedProductId =
        responseId && typeof responseId === "object" && "id" in responseId
          ? String((responseId as { id: string | number }).id)
          : (typeof responseId === "string" || typeof responseId === "number") &&
            String(responseId).trim()
          ? String(responseId)
          : product.id

      // Save translations if we have a product ID and translations to save
      if (onAfterSave && product.pendingTranslations && savedProductId) {
        const skuIds = product.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
        await onAfterSave(savedProductId, skuIds, product.pendingTranslations)
      }

      return { ...response, savedProductId }
    },
    onSuccess: (response, product) => {
      capturePlatformEvent("product_setup_completed", {
        category,
        is_new_product: !product.id,
        product_id: response.savedProductId ?? product.id,
        sku_count: product.colors?.length ?? 0,
      })
      if (options.showSuccessToast ?? true)
        showAlert({
          error: false,
          text: t("productPage.saveSuccess", {
            name: product.name,
            action: product.id ? t("productPage.updated") : t("productPage.created"),
          }),
        })
      clearCache()
      // The editor's own ?page carries the list page the merchant came from.
      if (options.navigateOnSuccess ?? true)
        navigate(withListPage(ProductCategoriesLink[category], location.search))
    },
    onError: () => {
      showAlert({
        error: true,
        text: getGenericUserErrorMessage(t),
      })
    },
  })
}

export const useToggleProductVisibilityMutation = (_params: ProductsQueryParams) => {
  const queryClient = useQueryClient()
  const { showAlert } = useAlert()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: async ({ id, visible }: Product) => {
      const response = await tintVTOService.toggleProductVisibility(id, !visible)
      return response
    },
    onMutate: async ({ id, visible }) => {
      await queryClient.cancelQueries({ queryKey: PRODUCTS_QUERY_KEY() })

      const previousProducts = snapshotProductQueries(queryClient)
      toggleProductVisibilityInCachedLists(queryClient, id, !visible)

      return { previousProducts }
    },
    onSuccess: (_, { visible }) => {
      showAlert({
        error: false,
        text: t("productsPage.visibilityToggle", {
          productName: "Product",
          visibility: !visible ? t("productsPage.hidden") : t("productsPage.visible"),
        }),
      })
      // The optimistic cache already reflects the toggle; an active refetch
      // here re-renders the whole table and reorders rows under the cursor
      // (rows shift, the "wrong" toggle appears to flip). Mark stale only.
      void queryClient.invalidateQueries({
        queryKey: PRODUCTS_QUERY_KEY(),
        refetchType: "none",
      })
      void queryClient.invalidateQueries({
        queryKey: ALL_PRODUCTS_QUERY_KEY,
        refetchType: "none",
      })
      void queryClient.invalidateQueries({
        queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY,
        refetchType: "none",
      })
    },
    onError: (_error, _variables, context) => {
      showAlert({ error: true, text: getGenericUserErrorMessage(t) })
      restoreProductQueries(queryClient, context?.previousProducts)
    },
  })
}

export const useDeleteProductMutation = (_params: ProductsQueryParams) => {
  const queryClient = useQueryClient()
  const { showAlert } = useAlert()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: async (product: ProductV2) => {
      const response = await tintVTOService.deleteProduct(product.id)
      return response
    },
    onMutate: async (product) => {
      await queryClient.cancelQueries({ queryKey: PRODUCTS_QUERY_KEY() })

      const previousProducts = snapshotProductQueries(queryClient)
      removeProductFromCachedLists(queryClient, product.id)

      return { previousProducts }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: PRODUCTS_QUERY_KEY(),
        refetchType: "none",
      })
      void queryClient.invalidateQueries({
        queryKey: ALL_PRODUCTS_QUERY_KEY,
        refetchType: "none",
      })
      void queryClient.invalidateQueries({
        queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY,
        refetchType: "none",
      })
    },
    onError: (_error, _variables, context) => {
      showAlert({ error: true, text: getGenericUserErrorMessage(t) })
      restoreProductQueries(queryClient, context?.previousProducts)
    },
  })
}
